import { clamp, getWindow } from 'ag-charts-core';
import type { AgTooltipAnchorTo, AgTooltipMode, AgTooltipPlacement, InteractionRange, TextWrap } from 'ag-charts-types';

import type { DOMManager } from '../../dom/domManager';
import type { LocaleManager } from '../../locale/localeManager';
import { Deprecated } from '../../util/deprecation';
import { type Bounds, type Placement, calculatePlacement } from '../../util/placement';
import { BaseProperties } from '../../util/properties';
import { SizeMonitor } from '../../util/sizeMonitor';
import {
    ARRAY_OF,
    BOOLEAN,
    INTERACTION_RANGE,
    NUMBER,
    OBJECT,
    OR,
    POSITIVE_NUMBER,
    TEXT_WRAP,
    TempValidate,
    UNION,
} from '../../util/validation';
import { SpringAnimation } from './springAnimation';
import {
    DEFAULT_TOOLTIP_CLASS,
    DEFAULT_TOOLTIP_DARK_CLASS,
    type TooltipContent,
    type TooltipPaginationState,
    tooltipHtml,
} from './tooltipContent';

export {
    DEFAULT_TOOLTIP_CLASS,
    DEFAULT_TOOLTIP_DARK_CLASS,
    tooltipHtml,
    tooltipContentAriaLabel,
    type TooltipContent,
    type TooltipPaginationState,
    type TooltipContentDataRow,
    type TooltipStructuredContent,
} from './tooltipContent';

type TooltipPositionType =
    | 'pointer'
    | 'node'
    | 'top'
    | 'right'
    | 'bottom'
    | 'left'
    | 'top-left'
    | 'top-right'
    | 'bottom-right'
    | 'bottom-left';

type TooltipOffsets = { canvasX: number; canvasY: number; nodeCanvasX?: number; nodeCanvasY?: number };
export type TooltipEventType = 'pointermove' | 'click' | 'dblclick' | 'keyboard';
export type TooltipPointerEvent<T extends TooltipEventType = TooltipEventType> = Readonly<TooltipOffsets> & {
    readonly type: T;
};

export interface TooltipMetaPosition {
    anchorTo?: AgTooltipAnchorTo;
    defaultAnchorTo?: AgTooltipAnchorTo;
    placement?: AgTooltipPlacement | AgTooltipPlacement[];
    defaultPlacement?: AgTooltipPlacement | AgTooltipPlacement[];
    xOffset?: number;
    yOffset?: number;
}

export interface TooltipMeta extends TooltipOffsets {
    showArrow?: boolean;
    position?: TooltipMetaPosition;
    enableInteraction?: boolean;
}

const horizontalAlignments: Record<AgTooltipPlacement, -1 | 0 | 1> = {
    left: -1,
    'top-left': -1,
    'bottom-left': -1,
    top: 0,
    center: 0,
    bottom: 0,
    right: 1,
    'top-right': 1,
    'bottom-right': 1,
};

const verticalAlignments: Record<AgTooltipPlacement, -1 | 0 | 1> = {
    'top-left': -1,
    top: -1,
    'top-right': -1,
    left: 0,
    center: 0,
    right: 0,
    'bottom-left': 1,
    bottom: 1,
    'bottom-right': 1,
};

enum ArrowPosition {
    Left,
    Top,
    Bottom,
    Right,
}

const arrowPositions: Record<AgTooltipPlacement, ArrowPosition | undefined> = {
    left: ArrowPosition.Right,
    'top-left': undefined,
    'bottom-left': undefined,
    top: ArrowPosition.Bottom,
    center: undefined,
    bottom: ArrowPosition.Top,
    right: ArrowPosition.Left,
    'top-right': undefined,
    'bottom-right': undefined,
};

enum DirectionCheck {
    Horizontal = 0b01,
    Vertical = 0b10,
    Both = 0b11,
    None = 0b00,
}

const directionChecks: Record<AgTooltipPlacement, DirectionCheck> = {
    top: DirectionCheck.Vertical,
    bottom: DirectionCheck.Vertical,
    left: DirectionCheck.Horizontal,
    right: DirectionCheck.Horizontal,
    'top-right': DirectionCheck.Both,
    'top-left': DirectionCheck.Both,
    'bottom-right': DirectionCheck.Both,
    'bottom-left': DirectionCheck.Both,
    center: DirectionCheck.None,
};

const TOOLTIP_MODE = UNION(['single', 'shared', 'compact']);

const POSITION_TYPE = UNION(
    ['pointer', 'node', 'top', 'right', 'bottom', 'left', 'top-left', 'top-right', 'bottom-right', 'bottom-left'],
    'a position type'
);

const ANCHOR_TO = UNION(['pointer', 'node', 'chart'], 'an anchorTo');

const PLACEMENT_UNION = UNION(
    ['top', 'right', 'bottom', 'left', 'top-right', 'bottom-right', 'bottom-left', 'top-left', 'center'],
    'a placement'
);
const PLACEMENT = OR(PLACEMENT_UNION, ARRAY_OF(PLACEMENT_UNION));

export class TooltipPosition extends BaseProperties {
    /**
     * @todo(AG-10870) - this should never be undefined, but there's something odd going on with
     * theming that doesn't look so easy to fix. This property will be removed in the next major,
     * so for now we'll just work around. It's marked as protected no code outside of this class
     * can use it, and all code will use the newer `placement` and `anchorTo` properties, which
     * derive their defaults from this property. Eventually those properties will be set via the
     * theme, and we'll make sure they are applied normally.
     */
    @TempValidate(POSITION_TYPE, { optional: true })
    @Deprecated('Use `anchorTo` and/or `placement` options instead.')
    /** The type of positioning for the tooltip. By default, the tooltip follows the pointer. */
    protected type?: TooltipPositionType;
    /** @todo Remove this when type is removed. */
    @TempValidate(POSITION_TYPE, { optional: true })
    protected _seriesOverrideType?: TooltipPositionType;

    @TempValidate(NUMBER)
    /** The horizontal offset in pixels for the position of the tooltip. */
    xOffset: number = 0;

    @TempValidate(NUMBER)
    /** The vertical offset in pixels for the position of the tooltip. */
    yOffset: number = 0;

    @TempValidate(ANCHOR_TO, { optional: true })
    anchorTo?: AgTooltipAnchorTo;

    @TempValidate(PLACEMENT, { optional: true })
    placement?: AgTooltipPlacement | AgTooltipPlacement[];

    get defaultAnchorTo(): AgTooltipAnchorTo {
        const { type, _seriesOverrideType } = this;
        const defaultType = type ?? _seriesOverrideType ?? 'pointer';

        if (defaultType === 'node' || defaultType === 'pointer') {
            return defaultType;
        } else {
            return 'chart';
        }
    }

    get defaultPlacement(): AgTooltipPlacement {
        const { type, _seriesOverrideType } = this;
        const defaultType = type ?? _seriesOverrideType ?? 'pointer';

        if (defaultType === 'node' || defaultType === 'pointer') {
            return 'top';
        }

        return defaultType;
    }
}

export class Tooltip extends BaseProperties {
    @TempValidate(BOOLEAN)
    enabled: boolean = true;

    @TempValidate(TOOLTIP_MODE)
    mode: AgTooltipMode = 'single';

    @TempValidate(BOOLEAN, { optional: true })
    showArrow?: boolean;

    @TempValidate(POSITIVE_NUMBER)
    delay: number = 0;

    @TempValidate(INTERACTION_RANGE, { optional: true })
    range?: InteractionRange = undefined;

    @TempValidate(TEXT_WRAP)
    wrapping: TextWrap = 'hyphenate';

    @TempValidate(OBJECT)
    readonly position = new TooltipPosition();

    @TempValidate(BOOLEAN)
    readonly pagination = false;

    @TempValidate(BOOLEAN)
    darkTheme = false;

    /** Escape-hatch for changes in AG-11645. */
    @TempValidate(UNION(['extended', 'canvas']))
    bounds: 'extended' | 'canvas' = 'extended';

    private readonly destroyFns: Array<() => void> = [];
    private readonly springAnimation = new SpringAnimation();

    private enableInteraction: boolean = false;
    private readonly wrapTypes = ['always', 'hyphenate', 'on-space', 'never'];

    private element?: HTMLElement;
    private readonly sizeMonitor = new SizeMonitor();

    // Reading the element size is expensive, so cache the result
    private _elementSize: { width: number; height: number } | undefined = undefined;
    private _showTimeout: NodeJS.Timeout | undefined = undefined;
    private arrowPosition: ArrowPosition | undefined = undefined;
    private _visible = false;

    private positionParams:
        | {
              canvasRect: DOMRect;
              relativeRect: { x: number; y: number; width: number; height: number };
              meta: TooltipMeta;
          }
        | undefined = undefined;

    get interactive() {
        return this.enableInteraction;
    }

    constructor() {
        super();

        this.destroyFns.push(this.springAnimation.addListener('update', this.updateTooltipPosition.bind(this)));
    }

    private localeManager: LocaleManager | undefined = undefined;
    setup(localeManager: LocaleManager, domManager: DOMManager) {
        if ('togglePopover' in getWindow<any>().HTMLElement.prototype) {
            this.element = domManager.addChild('tooltip-container', DEFAULT_TOOLTIP_CLASS);
            this.element.setAttribute('popover', 'manual');
            this.element.className = DEFAULT_TOOLTIP_CLASS;
            // @ts-expect-error Typings need updating
            this.element.style.positionAnchor = domManager.anchorName;

            this.sizeMonitor.observe(this.element, (size) => {
                this._elementSize = size;
                this.updateTooltipPosition();
            });
        }
        this.localeManager = localeManager;

        return () => {
            domManager.removeChild('tooltip-container', DEFAULT_TOOLTIP_CLASS);
            this.destroyFns.forEach((f) => f());

            if (this.element) {
                this.sizeMonitor.unobserve(this.element);
            }
        };
    }

    isVisible(): boolean {
        return this._visible;
    }

    contains(node: Node | null): boolean {
        return this.element?.contains(node) ?? false;
    }

    private updateTooltipPosition() {
        const { element, _elementSize: elementSize, positionParams } = this;
        if (element == null || elementSize == null || positionParams == null) return;

        const { canvasRect, relativeRect, meta } = positionParams;
        const { x: canvasX, y: canvasY } = this.springAnimation;

        let placements =
            meta.position?.placement ??
            this.position.placement ??
            meta.position?.defaultPlacement ??
            this.position.defaultPlacement;
        if (!Array.isArray(placements)) {
            placements = [placements];
        }
        const anchorTo =
            meta.position?.anchorTo ??
            this.position.anchorTo ??
            meta.position?.defaultAnchorTo ??
            this.position.defaultAnchorTo;
        const xOffset = meta.position?.xOffset ?? 0;
        const yOffset = meta.position?.yOffset ?? 0;

        const minX = relativeRect.x;
        const minY = relativeRect.y;
        const maxX = relativeRect.width - elementSize.width - 1 + minX;
        const maxY = relativeRect.height - elementSize.height + minY;

        let i = 0;
        let placement: AgTooltipPlacement | undefined;
        let position: Placement | undefined;
        let constrained = false;
        do {
            placement = placements[i];
            i += 1;

            const tooltipBounds = this.getTooltipBounds({
                elementSize,
                placement,
                anchorTo,
                canvasX,
                canvasY,
                yOffset,
                xOffset,
                canvasRect,
            });
            position = calculatePlacement(elementSize.width, elementSize.height, relativeRect, tooltipBounds);

            constrained = false;
            if (directionChecks[placement] & DirectionCheck.Horizontal) {
                constrained ||= position.x < minX || position.x > maxX;
            }
            if (directionChecks[placement] & DirectionCheck.Vertical) {
                constrained ||= position.y < minY || position.y > maxY;
            }
        } while (i < placements.length && constrained);

        const left = clamp(minX, position.x, maxX);
        const top = clamp(minY, position.y, maxY);

        constrained ||= left !== position.x || top !== position.y;
        const defaultShowArrow = anchorTo !== 'chart' && !constrained && !xOffset && !yOffset;
        const showArrow = meta.showArrow ?? this.showArrow ?? defaultShowArrow;
        this.arrowPosition = showArrow ? arrowPositions[placement] : undefined;
        this.updateClassModifiers();

        element.style.translate = `${left}px ${top}px`;
    }

    /**
     * Shows tooltip at the given event's coordinates.
     * If the `html` parameter is missing, moves the existing tooltip to the new position.
     */
    show(
        boundingRect: DOMRect,
        canvasRect: DOMRect,
        meta: TooltipMeta,
        content: TooltipContent[] | null,
        pagination?: TooltipPaginationState,
        instantly = false
    ) {
        const { element } = this;

        if (element != null && content != null && content.length !== 0) {
            const html = tooltipHtml(this.localeManager, content, this.mode, this.pagination ? pagination : undefined);
            if (html == null) {
                this.toggle(false);
                return;
            }

            element.innerHTML = html;
        } else if (element == null || element.innerHTML === '') {
            this.toggle(false);
            return;
        }

        const relativeRect = {
            x: boundingRect.x - canvasRect.x,
            y: boundingRect.y - canvasRect.y,
            width: boundingRect.width,
            height: boundingRect.height,
        };

        this.positionParams = {
            canvasRect,
            relativeRect,
            meta,
        };

        const anchorTo =
            meta.position?.anchorTo ??
            this.position.anchorTo ??
            meta.position?.defaultAnchorTo ??
            this.position.defaultAnchorTo;
        switch (anchorTo) {
            case 'node':
                this.springAnimation.update(meta.nodeCanvasX ?? meta.canvasX, meta.nodeCanvasY ?? meta.canvasY);
                break;
            case 'pointer':
                this.springAnimation.update(meta.canvasX, meta.canvasY);
                break;
            case 'chart':
                this.springAnimation.reset();
        }

        if (meta.enableInteraction) {
            this.enableInteraction = true;
            element.style.pointerEvents = 'auto';
            element.removeAttribute('aria-hidden');
            element.tabIndex = -1; // AG-14347 Allow interactive tooltips to receive focus
        } else {
            this.enableInteraction = false;
            element.style.pointerEvents = 'none';
            element.setAttribute('aria-hidden', 'true');
            element.removeAttribute('tabindex');
        }

        element.style.setProperty('--top', `${canvasRect.top}px`);
        element.style.setProperty('--left', `${canvasRect.left}px`);
        this.updateClassModifiers();

        this.toggle(true, instantly);
    }

    hide() {
        this.toggle(false);
    }

    private toggle(visible: boolean, instantly: boolean = false) {
        const { delay } = this;

        if (visible && delay > 0 && !instantly) {
            this._showTimeout ??= setTimeout(() => {
                this._showTimeout = undefined;
                this.toggleCallback(true);
            }, delay);
        } else {
            clearTimeout(this._showTimeout);
            this._showTimeout = undefined;
            this.toggleCallback(visible);
        }
    }

    private toggleCallback(visible: boolean) {
        if (!this.element?.isConnected) return;

        // Avoid touching the DOM if invisible and visibility status hasn't changed.
        if (this._visible === visible) return;
        this._visible = visible;

        this.element.togglePopover(visible);

        if (visible) {
            // We can only measure the element when it's actually visible
            // This removes a possible jump for the tooltip
            this.updateTooltipPosition();
        } else {
            this.springAnimation.reset();
        }
    }

    private updateClassModifiers() {
        if (!this.element?.isConnected) return;

        const { classList } = this.element;

        const toggleClass = (name: string, include: boolean) =>
            classList.toggle(`${DEFAULT_TOOLTIP_CLASS}--${name}`, include);

        toggleClass('no-interaction', !this.enableInteraction); // Prevent interaction.
        toggleClass('arrow-top', this.arrowPosition === ArrowPosition.Top);
        toggleClass('arrow-right', this.arrowPosition === ArrowPosition.Right);
        toggleClass('arrow-bottom', this.arrowPosition === ArrowPosition.Bottom);
        toggleClass('arrow-left', this.arrowPosition === ArrowPosition.Left);
        toggleClass('compact', this.mode === 'compact');

        classList.toggle(DEFAULT_TOOLTIP_DARK_CLASS, this.darkTheme);

        for (const wrapType of this.wrapTypes) {
            classList.toggle(`${DEFAULT_TOOLTIP_CLASS}--wrap-${wrapType}`, wrapType === this.wrapping);
        }
    }

    private getTooltipBounds(opts: {
        elementSize: { width: number; height: number };
        anchorTo: AgTooltipAnchorTo;
        placement: AgTooltipPlacement;
        canvasX: number;
        canvasY: number;
        yOffset: number;
        xOffset: number;
        canvasRect: DOMRect;
    }): Bounds {
        const { elementSize, anchorTo, placement, canvasX, canvasY, yOffset, xOffset, canvasRect } = opts;

        const { width: tooltipWidth, height: tooltipHeight } = elementSize;
        const bounds: Bounds = { width: tooltipWidth, height: tooltipHeight };

        if (anchorTo === 'node' || anchorTo === 'pointer') {
            const horizontalAlignment = horizontalAlignments[placement];
            const verticalAlignment = verticalAlignments[placement];
            bounds.top = canvasY + yOffset + (tooltipHeight * (verticalAlignment - 1)) / 2 + 8 * verticalAlignment;
            bounds.left = canvasX + xOffset + (tooltipWidth * (horizontalAlignment - 1)) / 2 + 8 * horizontalAlignment;
            return bounds;
        }

        switch (placement) {
            case 'top': {
                bounds.top = yOffset;
                bounds.left = canvasRect.width / 2 - tooltipWidth / 2 + xOffset;
                return bounds;
            }
            case 'right': {
                bounds.top = canvasRect.height / 2 - tooltipHeight / 2 + yOffset;
                bounds.left = canvasRect.width - tooltipWidth / 2 + xOffset;
                return bounds;
            }
            case 'left': {
                bounds.top = canvasRect.height / 2 - tooltipHeight / 2 + yOffset;
                bounds.left = xOffset;
                return bounds;
            }
            case 'bottom': {
                bounds.top = canvasRect.height - tooltipHeight + yOffset;
                bounds.left = canvasRect.width / 2 - tooltipWidth / 2 + xOffset;
                return bounds;
            }
            case 'top-left': {
                bounds.top = yOffset;
                bounds.left = xOffset;
                return bounds;
            }
            case 'top-right': {
                bounds.top = yOffset;
                bounds.left = canvasRect.width - tooltipWidth + xOffset;
                return bounds;
            }
            case 'bottom-right': {
                bounds.top = canvasRect.height - tooltipHeight + yOffset;
                bounds.left = canvasRect.width - tooltipWidth + xOffset;
                return bounds;
            }
            case 'bottom-left': {
                bounds.top = canvasRect.height - tooltipHeight + yOffset;
                bounds.left = xOffset;
                return bounds;
            }
        }

        return bounds;
    }
}
